import { useQuery } from '@tanstack/react-query'
import { MessageCircle } from 'lucide-react'
import { apiClient } from '../../api/client'
import type { ApiResponse, ConversationDTO } from '../../types'
import { formatDistanceToNow } from '../../utils/date'
import { useNavigate } from 'react-router-dom'
import Avatar from '../../components/Avatar'

export default function ChatPage() {
  const navigate = useNavigate()
  const { data: conversations, isLoading } = useQuery({
    queryKey: ['conversations'],
    queryFn: () =>
      apiClient.get<ApiResponse<ConversationDTO[]>>('/chat').then((r) => r.data.data),
  })

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <h1 className="font-display font-semibold text-[30px] text-ink-900 mb-5">Messages</h1>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="card h-16 animate-pulse" />)}
        </div>
      ) : conversations?.length ? (
        <div className="divide-y divide-gray-100 card overflow-hidden">
          {conversations.map((c) => (
            <button
              key={c.conversationId}
              onClick={() => navigate(`/chat/${c.conversationId}`)}
              className="w-full flex items-center gap-4 p-4 hover:bg-ivory-100/60 transition-colors text-left"
            >
              <Avatar url={c.otherUserAvatar} name={c.otherUserName} className="w-12 h-12" />
              <div className="flex-1 min-w-0">
                <div className="flex justify-between">
                  <p className="font-semibold text-ink-900 text-[15px]">{c.otherUserName ?? 'Unknown'}</p>
                  {c.lastMessageAt && <p className="text-[11px] text-ink-400">{formatDistanceToNow(c.lastMessageAt)}</p>}
                </div>
                <p className="text-xs text-ink-400 mt-0.5 truncate">{c.lastMessage ?? 'Say hello 🙏'}</p>
              </div>
              {c.unreadCount > 0 && (
                <span className="bg-maroon-700 text-champagne text-[10px] font-bold w-5 h-5 rounded-full ring-1 ring-gold-500/50 flex items-center justify-center flex-shrink-0">
                  {c.unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-ink-400">
          <MessageCircle strokeWidth={1.5} className="w-12 h-12 mx-auto mb-3 text-gold-600/40" />
          <p>No conversations yet</p>
          <p className="text-sm mt-1">Accept an interest to start chatting</p>
        </div>
      )}
    </div>
  )
}
